import React, { useMemo } from "react";
import { withTranslation } from "react-i18next";
import { FaCut, FaMapMarkedAlt, FaShieldAlt } from "react-icons/fa";

import { toggleSelectionId } from "./sectionReportApi";
import "./SectionMapPicker.css";

function SectionMapPicker({
  t,
  catalog,
  selectedSubFarmIds,
  selectedLandplotIds,
  onSubFarmChange,
  onLandplotChange,
}) {
  const subFarms = (catalog && catalog.sub_farms) || [];
  const landplots = (catalog && catalog.landplots) || [];

  const landplotsByFarm = useMemo(() => {
    const groups = new Map();
    landplots.forEach((plot) => {
      const key = plot.farm_name || t("Sem seção");
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key).push(plot);
    });
    return Array.from(groups.entries());
  }, [landplots, t]);

  if (!subFarms.length && !landplots.length) {
    return (
      <div className="section-map-picker section-map-picker--empty">
        <p>{t("Nenhum talhão ou sub-fazenda cadastrado no mapa da fazenda.")}</p>
        <p className="section-map-picker__hint">
          {t(
            "Cadastre perímetros em Mapa da fazenda → Sub-fazendas e talhões com recortes internos."
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="section-map-picker">
      {subFarms.length > 0 && (
        <section className="section-map-picker__group">
          <h3>{t("Sub-fazendas")}</h3>
          <p className="section-map-picker__hint">
            {t("Perímetros desenhados no mapa da fazenda (Desenhar fazenda).")}
          </p>
          <ul className="section-map-picker__list">
            {subFarms.map((farm) => {
              const checked = selectedSubFarmIds.includes(Number(farm.id));
              return (
                <li key={farm.id}>
                  <label className="section-map-picker__item">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() =>
                        onSubFarmChange(toggleSelectionId(selectedSubFarmIds, farm.id))
                      }
                    />
                    <span className="section-map-picker__item-body">
                      <span className="section-map-picker__item-title">
                        {farm.name}
                      </span>
                      <span className="section-map-picker__item-meta">
                        {farm.has_boundary ? (
                          <>
                            <FaMapMarkedAlt aria-hidden="true" />{" "}
                            {t("Cerca virtual — perímetro")}
                          </>
                        ) : (
                          t("Sem perímetro — usa talhões vinculados")
                        )}
                        {farm.landplot_count > 0
                          ? ` · ${farm.landplot_count} ${t("talhão(ões)")}`
                          : ""}
                      </span>
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {landplots.length > 0 && (
        <section className="section-map-picker__group">
          <h3>{t("Talhões e medições")}</h3>
          <p className="section-map-picker__hint">
            {t(
              "Cercas virtuais cadastradas no mapa. Recortes internos excluem área da contagem."
            )}
          </p>
          {landplotsByFarm.map(([farmName, plots]) => (
            <div key={farmName} className="section-map-picker__farm-group">
              <h4>{farmName}</h4>
              <ul className="section-map-picker__list">
                {plots.map((plot) => {
                  const checked = selectedLandplotIds.includes(Number(plot.id));
                  return (
                    <li key={plot.id}>
                      <label className="section-map-picker__item">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() =>
                            onLandplotChange(
                              toggleSelectionId(selectedLandplotIds, plot.id)
                            )
                          }
                        />
                        <span className="section-map-picker__item-body">
                          <span className="section-map-picker__item-title">
                            {plot.name}
                          </span>
                          <span className="section-map-picker__item-meta">
                            {plot.area_h2 > 0 ? `${plot.area_h2} ha` : null}
                            {plot.has_geofence ? (
                              <span className="section-map-picker__badge">
                                <FaShieldAlt aria-hidden="true" />{" "}
                                {t("Cerca virtual")}
                              </span>
                            ) : null}
                            {plot.hole_count > 0 ? (
                              <span className="section-map-picker__badge section-map-picker__badge--cut">
                                <FaCut aria-hidden="true" />{" "}
                                {plot.hole_count}{" "}
                                {plot.hole_count === 1
                                  ? t("recorte")
                                  : t("recortes")}
                              </span>
                            ) : null}
                          </span>
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}

export default withTranslation()(SectionMapPicker);
